import { InputField } from '@/components/form/InputField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Form } from '@/components/primitives/Form';
import { sendPasswordResetLinkLoadingSelector } from '@/features/loading/loadingSelectors';
import { sendPasswordResetLink } from '@/features/reset-password/resetPasswordActions';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { showErrorToast } from '@/utils/errorToast';
import {
  createPasswordResetLinkSchema,
  type PasswordResetLinkValues,
} from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { type FC, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';

interface PasswordResetLinkFormProps {}

const PasswordResetLinkForm: FC<PasswordResetLinkFormProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState<string>('');
  const loading = useSelector(sendPasswordResetLinkLoadingSelector);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  usePageTitle('resetPassword');

  const form = useForm<PasswordResetLinkValues>({
    resolver: zodResolver(createPasswordResetLinkSchema()),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = useCallback(
    async (values?: PasswordResetLinkValues) => {
      const currentEmail = values?.email || email;

      try {
        await dispatch(sendPasswordResetLink(currentEmail)).unwrap();

        setEmail(currentEmail);
        toast.success(<TransAlert i18nKey="passwordResetLinkSentToEmail" />);
      } catch (e) {
        console.log({ e });
        showErrorToast(
          <TransAlert i18nKey="failedToSendPasswordResetLink" />,
          e as ApiErrorResponse
        );
      }
    },
    [dispatch, email]
  );

  const renderEmailSubmit = () => (
    <>
      <Typography variant={isLaptopOrBigger ? 'heading2' : 'subtitle'}>
        <TransText i18nKey="insertYourEmail" />
      </Typography>
      <Typography variant="body1">
        <TransText i18nKey="willSendYouTheLink" />
      </Typography>
      <Divider />
      <Form
        form={form}
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex flex-col gap-6 [&_input]:h-12', {
          '[&_input]:h-8': isLaptopOrBigger,
        })}
      >
        <InputField
          name="email"
          required
          placeholder={t('text.enterYourEmailAddress')}
          label={<TransText i18nKey="email" />}
        />
        <Button
          data-testid="send-password-reset-link-button"
          type="submit"
          size="medium"
          startAdornment={<Icons.check />}
          isPending={loading}
        >
          <TransText i18nKey="confirm" />
        </Button>
      </Form>
    </>
  );

  const renderResend = () => (
    <>
      <Typography variant={isLaptopOrBigger ? 'heading2' : 'subtitle'}>
        <TransText i18nKey="checkYourEmail" />
      </Typography>
      <Typography variant="body1">
        <TransText i18nKey="weHaveSentYouTheLink" />
      </Typography>
      <Divider />

      <Button
        data-testid="send-password-reset-link-button"
        onClick={() => onSubmit()}
        variant="ghost"
        isPending={loading}
      >
        <TransText i18nKey="resendTheLinkViaEmail" />
      </Button>
    </>
  );

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-6 p-4 tablet:my-auto laptop:p-0">
        <Typography
          variant={isLaptopOrBigger ? 'title' : 'mobile-title'}
          className="pb-2"
        >
          <TransText i18nKey="resetYourPassword" />
        </Typography>

        <Card className="flex flex-col gap-4 rounded-xl">
          {!email ? renderEmailSubmit() : renderResend()}
        </Card>
      </div>
    </CenteredLayout>
  );
};

export default PasswordResetLinkForm;
